import {
  isAfterMaxTime,
  isBeforeMinTime,
  isValidHour,
  isValidMinute
} from '@library/utils/dateValidations';
import { useCallback, useMemo, useState } from 'react';
import type { DateTime } from 'luxon';
import type { TimeValidations } from '@library/types/dates';

interface TimePickerContextValidations {
  validateTime: ({
    inputName,
    newTime,
    value
  }: {
    inputName: 'hour' | 'minutes';
    newTime: DateTime;
    value: string;
  }) => void;
  validations: TimeValidations;
}

export const useTimePickerContextValidations = ({
  maxDate,
  minDate,
  uniqueStartTimes = []
}: {
  maxDate?: DateTime;
  minDate?: DateTime;
  uniqueStartTimes?: Array<string>;
}) => {
  const [isHourValid, setIsHourValid] = useState<boolean>(true);
  const [isMinuteValid, setIsMinuteValid] = useState<boolean>(true);
  const [isAfterMax, setIsAfterMax] = useState<boolean>(false);
  const [isBeforeMin, setIsBeforeMin] = useState<boolean>(false);
  const [isHourError, setIsHourError] = useState<boolean>(false);
  const [isMinuteError, setIsMinuteError] = useState<boolean>(false);

  const isError = isAfterMax || isBeforeMin || !isHourValid || !isMinuteValid;

  const setInputError = ({
    inputName,
    value
  }: {
    inputName: 'hour' | 'minutes';
    value: boolean;
  }) => {
    if (inputName === 'hour') {
      setIsHourError(value);
    } else {
      setIsMinuteError(value);
    }
  };

  const validateMinMaxDates = useCallback(
    ({
      inputName,
      newTime
    }: {
      inputName: 'hour' | 'minutes';
      newTime: DateTime;
    }) => {
      const isNowAfterMax = isAfterMaxTime({ maxDate, newTime });
      const isNowBeforeMin = isBeforeMinTime({ minDate, newTime });

      if (isNowAfterMax) {
        setIsAfterMax(true);
        setInputError({ inputName, value: true });
      } else if (isAfterMax && !isNowAfterMax) {
        setIsAfterMax(false);

        if (isHourValid && isMinuteValid) {
          setInputError({ inputName: 'hour', value: false });
          setInputError({ inputName: 'minutes', value: false });
        } else {
          setInputError({ inputName, value: false });
        }
      } else if (isNowBeforeMin) {
        setIsBeforeMin(true);
        setInputError({ inputName, value: true });
      } else if (isBeforeMin && !isNowBeforeMin) {
        setIsBeforeMin(false);

        if (isHourValid && isMinuteValid) {
          setInputError({ inputName: 'hour', value: false });
          setInputError({ inputName: 'minutes', value: false });
        } else {
          setInputError({ inputName, value: false });
        }
      }
    },
    [isAfterMax, isBeforeMin, isHourValid, isMinuteValid, maxDate, minDate]
  );

  const validateTimeFormat = useCallback(
    ({
      inputName,
      value
    }: {
      inputName: 'hour' | 'minutes';
      value: string;
    }) => {
      const validationFunction =
        inputName === 'hour' ? isValidHour : isValidMinute;
      const setValueValidation =
        inputName === 'hour' ? setIsHourValid : setIsMinuteValid;
      const isCurrentValueValid =
        inputName === 'hour' ? isHourValid : isMinuteValid;

      const validValue = validationFunction(value);

      if (!validValue) {
        setInputError({ inputName, value: true });
        setValueValidation(false);
      } else if (!isCurrentValueValid && validValue) {
        setInputError({ inputName, value: false });
        setValueValidation(true);
      }
    },
    [isHourValid, isMinuteValid]
  );

  const validateTime = useCallback(
    ({
      inputName,
      newTime,
      value
    }: {
      inputName: 'hour' | 'minutes';
      newTime: DateTime;
      value: string;
    }) => {
      validateMinMaxDates({ inputName, newTime });
      validateTimeFormat({ inputName, value });
    },
    [validateMinMaxDates, validateTimeFormat]
  );

  const timePickerContextValidations: TimePickerContextValidations =
    useMemo(() => {
      return {
        validateTime,
        validations: {
          isAfterMax,
          isBeforeMin,
          isError,
          isHourError,
          isHourValid,
          isMinuteError,
          isMinuteValid,
          uniqueStartTimes
        }
      };
    }, [
      isAfterMax,
      isBeforeMin,
      isError,
      isHourError,
      isHourValid,
      isMinuteError,
      isMinuteValid,
      validateTime,
      uniqueStartTimes
    ]);

  return timePickerContextValidations;
};
